import React, { useContext, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import UserProgressContext from '../../userProgressContext';
import { springSnap } from '../../utils/motion.config';

/**
 * DefenseScoreHUD — Persistent shield + score counter, fixed top-right.
 *
 * Reads totalScore from UserProgressContext.
 * Animates on score change with Framer Motion scale spring + green colour flash.
 * Mounted in App.js outside the route tree so it persists across all level routes.
 * Hidden on the home/login page (isUserProgressFetched = false).
 */
function DefenseScoreHUD() {
  const { totalScore, isUserProgressFetched } = useContext(UserProgressContext);
  const controls = useAnimation();
  const prevScoreRef = useRef(totalScore);

  useEffect(() => {
    if (!isUserProgressFetched) return;
    if (prevScoreRef.current !== totalScore) {
      prevScoreRef.current = totalScore;

      // Scale spring + green colour flash sequence
      controls.start({
        scale: [1, 1.35, 1],
        color: ['#ffffff', '#4ade80', '#ffffff'],
        transition: {
          scale: { ...springSnap, duration: 0.4 },
          color: { duration: 0.6, times: [0, 0.3, 1] },
        },
      });
    }
  }, [totalScore, controls, isUserProgressFetched]);

  // Only show while the user is in-game; hide on the home/login page.
  if (!isUserProgressFetched) return null;

  return (
    <motion.div
      animate={controls}
      className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-gray-900/80 backdrop-blur-sm border border-cyan-700 rounded-lg px-3 py-2 text-white shadow-lg select-none pointer-events-none"
      initial={{ scale: 1, color: '#ffffff' }}
    >
      {/* Shield SVG icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-5 h-5 text-cyan-400 flex-shrink-0"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M11.484 2.17a.75.75 0 0 1 1.032 0 11.209 11.209 0 0 0 7.877 3.08.75.75 0 0 1 .722.515 12.74 12.74 0 0 1 .635 3.985c0 5.942-4.064 10.933-9.563 12.348a.749.749 0 0 1-.374 0C6.314 20.683 2.25 15.692 2.25 9.75c0-1.39.223-2.73.635-3.985a.75.75 0 0 1 .722-.516l.143.001c2.996 0 5.718-1.17 7.734-3.08ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75ZM12 15a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z"
          clipRule="evenodd"
        />
      </svg>

      {/* Score label */}
      <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
        Score
      </span>

      {/* Score value */}
      <span className="text-sm font-bold tabular-nums min-w-[2.5rem] text-right">
        {totalScore}
      </span>
    </motion.div>
  );
}

export default DefenseScoreHUD;
